import { watch } from 'vue';
import {
  ACTIVITY_QUESTION_IDS,
  FUNCTION_QUESTION_IDS,
  calculateStartLevel,
  getQuizAnswerQueryKey,
} from '@quiz-configs/composables/personalization';
import type { FunnelStateModel } from '@core/base/utils/funnelStateCode';
import {
  FUNNEL_STATE_QUERY_KEY,
  createFunnelStateNonce,
  decodeFunnelState,
  encodeFunnelState,
  getGoalsByMain,
  getGoalsIndex,
} from '@core/base/utils/funnelStateCode';
import { isPreviewEnv } from '@core/base/utils/environment';

function normalizeQueryString(value: unknown): string {
  const single = Array.isArray(value)
    ? value.find(item => item != null && item !== '')
    : value;

  if (typeof single === 'string') {
    return single;
  }

  return typeof single === 'number' ? String(single) : '';
}

function readLegacyAnswer(query: Record<string, unknown>, questionIds: string[]): string | undefined {
  for (const questionId of questionIds) {
    const answer = normalizeQueryString(query[getQuizAnswerQueryKey(questionId)]);

    if (answer) {
      return answer;
    }
  }
}

function decodeLegacyState(query: Record<string, unknown>): Partial<FunnelStateModel> {
  const state: Partial<FunnelStateModel> = {};
  const ageId = normalizeQueryString(query.age);
  const goals = getGoalsByMain(normalizeQueryString(query.main_goal));

  if (ageId) {
    state.ageId = ageId;
  }

  if (goals) {
    state.goals = { ...goals };
  }

  if (normalizeQueryString(query.addGoal) === 'true') {
    state.addGoal = true;
  }

  const startLevel = calculateStartLevel({
    age: ageId,
    functionAnswer: readLegacyAnswer(query, FUNCTION_QUESTION_IDS),
    activityAnswer: readLegacyAnswer(query, ACTIVITY_QUESTION_IDS),
  });

  if (startLevel) {
    state.startLevel = startLevel;
  }

  return state;
}

export default defineNuxtPlugin({
  name: 'funnel-state',
  dependsOn: ['pinia-persistence'],

  setup(nuxtApp) {
    const store = useKegelAnalyticStore();
    const router = useRouter();
    const { $logger } = useNuxtApp();
    const { ENVIRONMENT } = useRuntimeConfig().public;

    const decoded = decodeFunnelState(store.funnelCode);
    const nonce = decoded?.nonce ?? createFunnelStateNonce();

    function getSavedAnswer(questionIds: string[]): number | undefined {
      const savedQuiz = (store.quiz || {}) as Record<string, number | undefined>;

      for (const questionId of questionIds) {
        const answer = savedQuiz[questionId];

        if (answer) {
          return answer;
        }
      }
    }

    function syncStartLevel(): void {
      const startLevel = nuxtApp.runWithContext(() => calculateStartLevel({
        age: store.ageId,
        functionAnswer: getSavedAnswer(FUNCTION_QUESTION_IDS),
        activityAnswer: getSavedAnswer(ACTIVITY_QUESTION_IDS),
      })) as number | undefined;

      if (startLevel) {
        store.startLevel = startLevel;
      }
    }

    function syncFunnelCode(): void {
      if (store.goals?.main && !getGoalsIndex(store.goals)) {
        $logger?.error(new Error('Funnel state code is missing a goal mapping'), {
          tags: {
            flow: 'funnel_state',
            step: 'encode',
          },
          extra: {
            main: store.goals.main,
          },
        });
      }

      const state: Partial<FunnelStateModel> = {
        ageId: store.ageId,
        goals: store.goals,
        addGoal: store.addGoal,
        startLevel: store.startLevel,
      };
      const hasState = Boolean(state.ageId || state.goals?.main || state.addGoal || state.startLevel);

      store.funnelCode = hasState ? encodeFunnelState(state, nonce) : '';

      if (isPreviewEnv(ENVIRONMENT)) {
        // eslint-disable-next-line no-console
        console.info('[funnel-state] code updated', { code: store.funnelCode, state });
      }

      syncQueryCode();
    }

    function syncQueryCode(): void {
      const url = new URL(globalThis.location.href);

      if ((url.searchParams.get(FUNNEL_STATE_QUERY_KEY) ?? '') === store.funnelCode) {
        return;
      }

      if (store.funnelCode) {
        url.searchParams.set(FUNNEL_STATE_QUERY_KEY, store.funnelCode);
      } else {
        url.searchParams.delete(FUNNEL_STATE_QUERY_KEY);
      }

      globalThis.history.replaceState(globalThis.history.state, '', url);
    }

    const restored = decoded?.state
      ?? decodeLegacyState(router.currentRoute.value.query);

    if (restored.ageId) {
      store.ageId = restored.ageId;
    }

    if (restored.goals) {
      store.goals = restored.goals;
    }

    if (restored.addGoal) {
      store.addGoal = restored.addGoal;
    }

    if (restored.startLevel) {
      store.startLevel = restored.startLevel;
    }

    syncStartLevel();
    syncFunnelCode();

    watch(
      [
        () => store.ageId,
        () => store.goals,
        () => store.addGoal,
        () => store.quiz,
      ],
      () => {
        syncStartLevel();
        syncFunnelCode();
      },
      { flush: 'sync', deep: true },
    );

    watch(() => store.startLevel, syncFunnelCode, { flush: 'sync' });

    router.afterEach(syncQueryCode);
  },
});
